import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col } from "reactstrap";
import MetaTags from 'react-meta-tags';
import { useAuth } from '../../store/auth';
import { vrfContent } from "../../data";
import { vrfContentArabic } from "../../data/indexArabic";

const Vrf = () => {
    const { isArabic } = useAuth();
    const data = isArabic ? vrfContent : vrfContentArabic;

    // --- Modernized State Management ---
    const [activeSection, setActiveSection] = useState('');

    // --- Data Structuring & Refs for Scrolling ---
    const sectionOrder = [
        'introduction', 'whatIsVrf', 'components', 'features', 'installation',
        'applications', 'cost', 'capabilities', 'conditioning', 'maintenance', 'system'
    ];
    const sectionRefs = useRef(sectionOrder.reduce((acc, value) => {
        const dataKey = value === 'introduction' ? 'introductionTitle' : value;
        acc[dataKey] = React.createRef();
        return acc;
    }, {}));

    // --- Effects for Navbar & Scrollspy ---
    useEffect(() => {
        const handleGlobalNavScroll = () => {
            const topnav = document.getElementById("topnav");
            if (topnav) topnav.classList.toggle("nav-sticky", window.scrollY > 80);
        };
        const handleScrollSpy = () => {
            const currentPosition = window.scrollY + 150;
            let currentSectionId = '';
            sectionOrder.forEach(key => {
                const dataKey = key === 'introduction' ? 'introductionTitle' : key;
                const section = sectionRefs.current[dataKey]?.current;
                if (section && section.offsetTop <= currentPosition) {
                    currentSectionId = dataKey;
                }
            });
            if (currentSectionId !== activeSection) {
                setActiveSection(currentSectionId);
            }
        };
        window.addEventListener('scroll', handleGlobalNavScroll);
        window.addEventListener('scroll', handleScrollSpy);
        return () => {
            window.removeEventListener('scroll', handleGlobalNavScroll);
            window.removeEventListener('scroll', handleScrollSpy);
        };
    }, [activeSection]);

    // --- Event Handlers ---
    const handleNavClick = (event, sectionId) => {
        event.preventDefault();
        const targetElement = document.getElementById(sectionId);
        if (targetElement) {
            const navbarHeight = document.getElementById('topnav')?.offsetHeight || 80;
            const offsetPosition = targetElement.offsetTop - navbarHeight - 20;
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
    };

    // --- Helper to get section titles ---
    const getSectionTitle = (key) => {
        if (key === 'introductionTitle') return data?.introductionTitle;
        return data[key]?.title;
    };

    return (
        <React.Fragment>
            <MetaTags>
                <title>VRF Systems | Subul Contracting</title>
            </MetaTags>

            <section className="bg-half-170 bg-light d-table w-100">
                <Container>
                    <Row className="mt-5 justify-content-center">
                        <Col lg={12} className="text-center">
                            <div className="pages-heading">
                                <h1 className="title mb-0">{data?.title}</h1>
                            </div>
                        </Col>
                    </Row>
                    <div className="position-breadcrumb">
                        <nav aria-label="breadcrumb" className="d-inline-block">
                            <ul className="breadcrumb rounded shadow mb-0 px-4 py-2">
                                {data?.breadcrumb?.map((item, index) => (
                                    <li className={item?.className} key={index}>
                                        {item?.link ? <Link to={item.link}>{item.name}</Link> : item?.name}
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </Container>
            </section>

            <div className="position-relative">
                <div className="shape overflow-hidden text-white">
                    <svg viewBox="0 0 2880 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z" fill="currentColor"></path>
                    </svg>
                </div>
            </div>

            <section className="section">
                <Container>
                    <Row>
                        {/* Sidebar Navigation */}
                        <Col lg={4} md={5} className="d-none d-md-block">
                            <div className="position-sticky" style={{ top: '100px' }}>
                                <div className="content-nav">
                                    <ul className="nav nav-pills flex-column">
                                        {sectionOrder.map(key => {
                                            const dataKey = key === 'introduction' ? 'introductionTitle' : key;
                                            return (
                                                <li className="nav-item" key={dataKey}>
                                                    <a href={`#${dataKey}`} onClick={(e) => handleNavClick(e, dataKey)} className={`nav-link ${activeSection === dataKey ? 'active' : ''}`}>
                                                        {getSectionTitle(dataKey)}
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        </Col>

                        {/* Main Content */}
                        <Col lg={8} md={7}>
                            <div id="introductionTitle" ref={sectionRefs.current.introductionTitle} className="mb-5">
                                <h4 className="card-title mb-3">{data?.introductionTitle}</h4>
                                <p className="text-muted">{data?.introduction}</p>
                            </div>

                            <div id="whatIsVrf" ref={sectionRefs.current.whatIsVrf} className="mb-5">
                                <h4 className="card-title mb-3">{data?.whatIsVrf.title}</h4>
                                {data?.whatIsVrf.text.map((p, i) => <p className="text-muted" key={i}>{p}</p>)}
                            </div>

                            <div id="components" ref={sectionRefs.current.components} className="mb-5">
                                <h4 className="card-title mb-3">{data?.components.title}</h4>
                                {data?.components.types.map((item, i) => (
                                    <div key={i}><p className="text-muted mb-2"><b>{item.title}</b>: {item.text}</p></div>
                                ))}
                            </div>

                            <div id="features" ref={sectionRefs.current.features} className="mb-5">
                                <h4 className="card-title mb-3">{data?.features.title}</h4>
                                {data?.features.types.map((item, i) => (
                                     <div key={i}><p className="text-muted mb-2"><b>{item.title}</b>: {item.text}</p></div>
                                ))}
                            </div>
                            
                            {/* Render other sections similarly */}
                            <div id="installation" ref={sectionRefs.current.installation} className="mb-5">
                                <h4 className="card-title mb-3">{data?.installation.title}</h4>
                                <p className="text-muted fw-bold">{data?.installation.boldText}</p>
                                {data?.installation.types.map((item, i) => (
                                     <div key={i}><p className="text-muted mb-2"><b>{item.title}</b>: {item.text}</p></div>
                                ))}
                            </div>
                            
                            <div id="applications" ref={sectionRefs.current.applications} className="mb-5">
                                <h4 className="card-title mb-3">{data?.applications.title}</h4>
                                <p className="text-muted fw-bold">{data?.applications.boldText}</p>
                                {data?.applications.types.map((item, i) => (
                                     <div key={i}><p className="text-muted mb-2"><b>{item.title}</b>: {item.text}</p></div>
                                ))}
                            </div>

                            <div id="cost" ref={sectionRefs.current.cost} className="mb-5">
                                <h4 className="card-title mb-3">{data?.cost.title}</h4>
                                {data?.cost.text.map((p, i) => <p className="text-muted" key={i}>{p}</p>)}
                            </div>

                            <div id="capabilities" ref={sectionRefs.current.capabilities} className="mb-5">
                                <h4 className="card-title mb-3">{data?.capabilities.title}</h4>
                                {data?.capabilities.text.map((p, i) => <p className="text-muted" key={i}>{p}</p>)}
                            </div>

                            <div id="conditioning" ref={sectionRefs.current.conditioning} className="mb-5">
                                <h4 className="card-title mb-3">{data?.conditioning.title}</h4>
                                {data?.conditioning.text.map((p, i) => <p className="text-muted" key={i}>{p}</p>)}
                            </div>
                            
                            <div id="maintenance" ref={sectionRefs.current.maintenance} className="mb-5">
                                <h4 className="card-title mb-3">{data?.maintenance.title}</h4>
                                {data?.maintenance.text.map((p, i) => <p className="text-muted" key={i}>{p}</p>)}
                            </div>
                            
                            <div id="system" ref={sectionRefs.current.system} className="mb-5">
                                <h4 className="card-title mb-3">{data?.system.title}</h4>
                                {data?.system.text.map((p, i) => <p className="text-muted" key={i}>{p}</p>)}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    );
}
export default Vrf;